import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MainURL from '../../../MainURL';
import Loading from '../../../components/Loading';
import './RecruitApplyModal.scss';
import type { RecruitApplyType } from '../../../types/application';
import type { ApplyChannel } from './useApplyChannel';

interface ResumeListItem {
  id: string;
  title: string;
  updatedAt: string;
}

interface RecruitApplyModalProps {
  isOpen: boolean;
  onClose: () => void;
  recruitId: string;
  /** 국내(minister) / 해외(overseas) 사역지 공고 구분 */
  recruitType?: RecruitApplyType;
  userAccount: string;
  isLogin: boolean;
  church: string;
  channel: ApplyChannel | null;
  onApplied?: (applicationId: string) => void;
}

interface FileApplyResult {
  applicationId: number;
  /** 가져온 공고의 예약 지원 — 교회가 인수할 때 전달 */
  reserved?: boolean;
  relayTo: string | null;
  statusUrl: string;
  canConvert: boolean;
}

// 다음 지원 때 다시 입력하지 않도록 이 브라우저에만 기억 (서버 저장 아님)
const CONTACT_KEY = 'applyContact';
const loadContact = () => {
  try {
    return JSON.parse(localStorage.getItem(CONTACT_KEY) || '{}') as { name?: string; email?: string; phone?: string };
  } catch {
    return {};
  }
};
const saveContact = (value: { name: string; email: string; phone: string }) => {
  try {
    localStorage.setItem(CONTACT_KEY, JSON.stringify(value));
  } catch {
    // 저장소 접근 불가 — 무시
  }
};

const formatSize = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(bytes / 1024))}KB`);
// 앱(comministermore)의 WebApplyScreen 안에서 열렸는지 — 앱이 지원현황·로그인을 대신 처리한다
const inApp = () => typeof window !== 'undefined' && typeof (window as any).ReactNativeWebView !== 'undefined';
const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());
// 제3자 제공 동의 문구 버전 — 문구를 바꾸면 올린다 (server applyRelay.js CONSENT_VERSION)
const CONSENT_VERSION = '2026-10-14';

/** 개인정보 제3자 제공 동의 — 이력서를 지원한 교회에 제공 (application-structure-final.md 8장 1번) */
function ApplyConsent({ church, kind, reserve, checked, onChange }: {
  church: string;
  kind: 'file' | 'resume';
  reserve: boolean;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  const items = kind === 'file'
    ? '이력서 파일, 이름·연락처·이메일, 교회에 전할 말'
    : '선택한 이력서의 내용(학력·경력·자기소개 등). 실명·연락처는 교회가 연락을 요청할 때 제공';
  return (
    <div className="recruit-apply-modal__consent-box">
      <strong>개인정보 제3자 제공 동의 (필수)</strong>
      <dl>
        <dt>제공받는 자</dt>
        <dd>{church || '이 공고를 올린 교회'}</dd>
        <dt>목적</dt>
        <dd>사역자 채용(청빙) 검토 및 지원자 연락</dd>
        <dt>항목</dt>
        <dd>{items}</dd>
        <dt>보유 기간</dt>
        <dd>교회의 채용 절차가 끝나거나 지원을 취소할 때까지</dd>
      </dl>
      {reserve && <p>교회가 아직 공고를 확인하지 않아, 교회가 확인(인수)한 뒤에 제공됩니다.</p>}
      <p>
        동의하지 않으셔도 되지만, 그 경우 사역자모아로는 지원할 수 없습니다. 공고에 이메일이 있으면 내 메일로 직접 보내실 수 있습니다.{' '}
        <a href="https://www.ministermore.co.kr/personalinfo.html" target="_blank" rel="noopener noreferrer">개인정보처리방침</a>
      </p>
      <label className="recruit-apply-modal__consent">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span>위 내용을 확인했고, {church || '교회'}에 제공하는 데 동의합니다.</span>
      </label>
    </div>
  );
}

export default function RecruitApplyModal({ isOpen, onClose, recruitId, recruitType = 'minister', userAccount, isLogin, church, channel, onApplied }: RecruitApplyModalProps) {
  const navigate = useNavigate();
  const canFile = Boolean(channel?.fileApply);
  const canResume = Boolean(channel?.resumeApply);
  // 가져온 공고의 예약 지원 (aggregated-posting-lock-design.md 4장) — 교회가 아직 확인하지 않았음을 숨기지 않는다
  const reserve = channel?.applyMode === 'RESERVE';
  const [tab, setTab] = useState<'file' | 'resume'>('file');

  // 이력서로 지원
  const [resumeList, setResumeList] = useState<ResumeListItem[]>([]);
  const [selectedResumeId, setSelectedResumeId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 파일로 지원
  const [files, setFiles] = useState<File[]>([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<FileApplyResult | null>(null);
  const [converting, setConverting] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const rules = channel?.fileRules;

  useEffect(() => {
    if (!isOpen) return;
    setTab(canFile ? 'file' : 'resume');
    setResult(null);
    setError('');
    setFiles([]);
    setMessage('');
    setConsent(false);
    const saved = loadContact();
    setName(saved.name || '');
    setEmail(saved.email || (isEmail(userAccount) ? userAccount : ''));
    setPhone(saved.phone || '');
  }, [isOpen, canFile, userAccount]);

  // 탭마다 제공 항목이 다르므로 동의를 다시 받는다
  useEffect(() => {
    setConsent(false);
  }, [tab]);

  useEffect(() => {
    if (!isOpen || !isLogin || !userAccount) return;

    setSelectedResumeId('');
    setLoading(true);
    axios.get(`${MainURL}/resume/getresumelist/${userAccount}`)
      .then(res => {
        if (res.data.success && Array.isArray(res.data.resumeList)) {
          setResumeList(res.data.resumeList);
        } else {
          setResumeList([]);
        }
      })
      .catch(err => {
        console.error('이력서 목록 조회 실패:', err);
        setResumeList([]);
      })
      .finally(() => setLoading(false));
  }, [isOpen, isLogin, userAccount]);

  if (!isOpen) return null;

  const addFiles = (list: FileList | null) => {
    if (!list || !rules) return;
    setError('');
    const next = [...files];
    for (const file of Array.from(list)) {
      const ext = `.${file.name.split('.').pop()?.toLowerCase() || ''}`;
      if (!rules.extensions.includes(ext)) {
        setError(`${rules.extensions.join(', ')} 파일만 올릴 수 있습니다.`);
        continue;
      }
      if (file.size > rules.maxFileMb * 1024 * 1024) {
        setError(`파일 하나는 ${rules.maxFileMb}MB 이하여야 합니다.`);
        continue;
      }
      if (next.length >= rules.maxFiles) {
        setError(`파일은 ${rules.maxFiles}개까지 올릴 수 있습니다.`);
        break;
      }
      if (!next.some(f => f.name === file.name && f.size === file.size)) next.push(file);
    }
    setFiles(next);
  };

  const fileFormError = () => {
    if (!files.length) return '이력서 파일을 올려 주세요.';
    if (rules && files.reduce((sum, f) => sum + f.size, 0) > rules.maxTotalMb * 1024 * 1024) return `파일 합계는 ${rules.maxTotalMb}MB 이하여야 합니다.`;
    if (!name.trim()) return '이름을 입력해 주세요.';
    if (!isEmail(email)) return '회신받을 이메일을 정확히 입력해 주세요.';
    if (phone.replace(/\D/g, '').length < 9) return '연락처를 입력해 주세요.';
    if (!consent) return '개인정보 제3자 제공(지원한 교회)에 동의해 주세요.';
    return '';
  };

  const handleFileSubmit = async () => {
    if (submitting) return;
    const problem = fileFormError();
    if (problem) {
      setError(problem);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const form = new FormData();
      form.append('recruitType', recruitType);
      form.append('recruitId', recruitId);
      form.append('name', name.trim());
      form.append('email', email.trim());
      form.append('phone', phone.trim());
      form.append('message', message.trim());
      form.append('consent', 'true');
      form.append('consentVersion', CONSENT_VERSION);
      form.append('website', website);
      files.forEach(f => form.append('files', f));
      const res = await axios.post(`${MainURL}/applyrelay/apply`, form);
      if (res.data.success) {
        saveContact({ name: name.trim(), email: email.trim(), phone: phone.trim() });
        setResult(res.data as FileApplyResult);
        onApplied?.(String(res.data.applicationId));
      } else {
        setError(res.data.error || '지원에 실패했습니다.');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || '지원에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResumeSubmit = async () => {
    if (!selectedResumeId || submitting) return;
    if (!consent) {
      alert('개인정보 제3자 제공(지원한 교회)에 동의해 주세요.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${MainURL}/applications/apply`, {
        recruitId,
        recruitType,
        resumeId: selectedResumeId,
        userAccount,
        consent: true,
        consentVersion: CONSENT_VERSION,
      });

      if (res.data.success) {
        onApplied?.(res.data.applicationId as string);
        onClose();
        const doneMessage = res.data.reserved
          ? '관심 표시를 남겼습니다.\n교회가 사역자모아에서 공고를 확인하면 전달됩니다.\n지원현황에서 진행 상태를 확인할까요?'
          : '지원이 완료되었습니다.\n지원현황에서 진행 상태를 확인할까요?';
        // 지원 직후 지원현황으로 안내 (application-status-design.md 4장 진입점)
        if (window.confirm(doneMessage)) {
          navigate(res.data.applicationId ? `/mypage/applications/${res.data.applicationId}` : '/mypage/applications');
        }
      } else {
        alert(res.data.error || '지원에 실패했습니다.');
      }
    } catch (error: any) {
      alert(error.response?.data?.error || '지원에 실패했습니다.');
      console.error('지원하기 실패:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // 업로드한 파일 → 사이트 이력서 초안 (Progressive Profiling 2·3단계)
  const handleConvert = async () => {
    if (!result || converting) return;
    setConverting(true);
    try {
      const res = await axios.post(`${MainURL}/applyrelay/parse/${result.applicationId}`);
      if (res.data.success) {
        onClose();
        navigate('/mypage/resumeedit', { state: { prefill: { ...res.data.fill, sourceFile: res.data.sourceFile } } });
      } else {
        alert(res.data.error || '자동 변환에 실패했습니다.');
      }
    } catch (err: any) {
      alert(err.response?.data?.error || '자동 변환에 실패했습니다.');
    } finally {
      setConverting(false);
    }
  };

  const goTo = (path: string) => {
    onClose();
    navigate(path);
  };

  if (result) {
    return (
      <div className="recruit-apply-modal-overlay" onClick={onClose}>
        <div className="recruit-apply-modal" onClick={(e) => e.stopPropagation()}>
          <div className="recruit-apply-modal__body recruit-apply-modal__done">
            <div className="recruit-apply-modal__done-mark">✓</div>
            <div className="recruit-apply-modal__title">{result.reserved ? '관심 표시를 남겼습니다' : '지원이 접수되었습니다'}</div>
            {result.reserved ? (
              <p>
                {church || '교회'}는 아직 사역자모아에서 이 공고를 확인하지 않았습니다. 교회가 확인(인수)하면 이력서가 바로 전달되고,
                30일 안에 확인되지 않으면 원문으로 직접 지원하시도록 이메일로 안내해 드립니다.
              </p>
            ) : (
              <p>
                지원 내역을 사역자모아에 기록해 두었습니다. {church || '교회'} 담당자{result.relayTo ? `(${result.relayTo})` : ''}에게 새 지원자 알림을 보냈고,
                이력서는 교회가 사역자모아에서 확인합니다. 교회의 회신은 입력하신 이메일로 직접 옵니다.
              </p>
            )}
            <p className="recruit-apply-modal__muted">
              {result.reserved
                ? '전달되면 이메일로 알려 드립니다.'
                : '사역자모아는 교회의 메일함을 볼 수 없어, 교회가 지원서를 읽었는지는 알려 드릴 수 없습니다. 교회가 사역자모아에서 연락·면접 등으로 상태를 바꾸면 지원 상태에 기록됩니다.'}
              {!isLogin && ' 지원 내역 링크를 확인 메일로도 보내 드렸어요.'}
            </p>
            {result.canConvert && isLogin && (
              <div className="recruit-apply-modal__suggest">
                <strong>이 파일을 사이트 이력서로 옮겨 드릴까요?</strong>
                <span>내용을 자동으로 읽어 양식에 채워 드립니다. 확인만 하고 저장하면 다음 지원은 클릭 한 번이면 됩니다.</span>
                <button type="button" className="recruit-apply-modal__btn recruit-apply-modal__btn--primary" onClick={handleConvert} disabled={converting}>
                  {converting ? '읽는 중...' : '예, 옮겨 주세요'}
                </button>
              </div>
            )}
            {!isLogin && !inApp() && (
              <div className="recruit-apply-modal__suggest">
                <strong>다음 지원은 더 간단하게</strong>
                <span>카카오로 3초 만에 시작하면 지원 내역이 한곳에 모이고, 올린 이력서를 사이트 이력서로 옮겨 둘 수 있습니다.</span>
                <button type="button" className="recruit-apply-modal__btn recruit-apply-modal__btn--secondary" onClick={() => goTo('/login')}>
                  카카오로 시작하기
                </button>
              </div>
            )}
          </div>
          <div className="recruit-apply-modal__footer">
            <button type="button" className="recruit-apply-modal__btn recruit-apply-modal__btn--secondary" onClick={onClose}>
              닫기
            </button>
            <button type="button" className="recruit-apply-modal__btn recruit-apply-modal__btn--primary" onClick={() => goTo(result.statusUrl)}>
              지원 상태 보기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="recruit-apply-modal-overlay" onClick={onClose}>
      <div className="recruit-apply-modal" onClick={(e) => e.stopPropagation()}>
        <div className="recruit-apply-modal__header">
          <div>
            <div className="recruit-apply-modal__title">{church ? `${church}에 ${reserve ? '관심 표시하기' : '지원하기'}` : reserve ? '관심 표시하기' : '지원하기'}</div>
            <div className="recruit-apply-modal__subtitle">
              {reserve
                ? '교회가 아직 사역자모아에서 확인하지 않은 공고입니다. 관심 표시는 교회가 확인하면 전달되며, 30일 내 확인되지 않으면 원문으로 직접 지원하시길 안내드립니다.'
                : '지원서는 사역자모아에 접수·기록되고, 교회에는 새 지원자 알림이 갑니다.'}
            </div>
          </div>
          <button type="button" className="recruit-apply-modal__close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </div>

        {canFile && canResume && (
          <div className="recruit-apply-modal__tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'file'} className={tab === 'file' ? 'active' : ''} onClick={() => setTab('file')}>
              이력서 파일로 지원
            </button>
            <button type="button" role="tab" aria-selected={tab === 'resume'} className={tab === 'resume' ? 'active' : ''} onClick={() => setTab('resume')}>
              사이트 이력서로 지원
            </button>
          </div>
        )}

        <div className="recruit-apply-modal__body">
          {tab === 'file' ? (
            <div className="recruit-apply-modal__form">
              <div
                className={`recruit-apply-modal__drop ${dragOver ? 'over' : ''}`}
                onClick={() => fileInput.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInput.current?.click(); }}
              >
                <strong>가지고 있는 이력서 파일을 그대로 올리세요</strong>
                <span>PDF · HWP · DOCX · 이미지 / 최대 {rules?.maxFiles ?? 3}개 · 파일당 {rules?.maxFileMb ?? 10}MB</span>
                <span className="recruit-apply-modal__drop-btn">파일 선택</span>
                <input
                  ref={fileInput}
                  type="file"
                  multiple
                  accept={rules?.extensions.join(',')}
                  style={{ display: 'none' }}
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                />
              </div>
              {files.length > 0 && (
                <ul className="recruit-apply-modal__files">
                  {files.map((f, i) => (
                    <li key={`${f.name}-${f.size}`}>
                      <span className="name">{f.name}</span>
                      <span className="size">{formatSize(f.size)}</span>
                      <button type="button" aria-label={`${f.name} 빼기`} onClick={() => setFiles(files.filter((_, j) => j !== i))}>×</button>
                    </li>
                  ))}
                </ul>
              )}
              {channel?.methods.includes('post') || channel?.methods.includes('visit') ? (
                <p className="recruit-apply-modal__muted">이 공고는 우편·방문 제출도 안내하고 있습니다. 원본 서류가 필요한지 교회 안내를 함께 확인하세요.</p>
              ) : null}

              <div className="recruit-apply-modal__fields">
                <label>
                  <span>이름</span>
                  <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} autoComplete="name" />
                </label>
                <label>
                  <span>연락처</span>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="010-0000-0000" autoComplete="tel" />
                </label>
                <label className="wide">
                  <span>회신받을 이메일</span>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="email" />
                </label>
                <label className="wide">
                  <span>교회에 전할 말 (선택)</span>
                  <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} rows={3} placeholder="예) 안녕하십니까. 공고를 보고 지원합니다. 다음 달부터 사역 가능합니다." />
                </label>
                {/* 봇 차단용 — 사람에게는 보이지 않는다 */}
                <input className="recruit-apply-modal__hp" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} aria-hidden="true" />
              </div>
              <ApplyConsent church={church} kind="file" reserve={reserve} checked={consent} onChange={setConsent} />
              {error && <p className="recruit-apply-modal__error">{error}</p>}
            </div>
          ) : !isLogin ? (
            <div className="recruit-apply-modal__empty">
              <p>로그인하면 사이트에 저장한 이력서로 바로 지원할 수 있습니다.</p>
              <button type="button" className="recruit-apply-modal__btn recruit-apply-modal__btn--primary" onClick={() => goTo('/login')}>
                로그인하기
              </button>
            </div>
          ) : loading ? (
            <div className="recruit-apply-modal__loading">
              <Loading />
            </div>
          ) : resumeList.length === 0 ? (
            <div className="recruit-apply-modal__empty">
              <p>저장한 이력서가 없습니다.{canFile ? ' 가지고 있는 이력서 파일로 바로 지원할 수 있어요.' : ''}</p>
              {canFile ? (
                <button type="button" className="recruit-apply-modal__btn recruit-apply-modal__btn--primary" onClick={() => setTab('file')}>
                  파일로 지원하기
                </button>
              ) : (
                <button type="button" className="recruit-apply-modal__btn recruit-apply-modal__btn--primary" onClick={() => goTo('/mypage/resumeedit')}>
                  이력서 작성하러 가기
                </button>
              )}
            </div>
          ) : (
            <>
              {/* 비공개 이력서도 지원한 교회에는 스냅샷이 공개됨을 고지 (설계서 10장 7번) */}
              <p className="recruit-apply-modal__muted">한 번 저장한 이력서로 여러 공고에 바로 지원할 수 있습니다. 지원 내역은 마이페이지 지원현황에 모입니다.</p>
              <div className="recruit-apply-modal__resume-list">
                {resumeList.map((resume) => (
                  <div
                    key={resume.id}
                    className={`recruit-apply-modal__resume-item ${selectedResumeId === resume.id ? 'selected' : ''}`}
                    onClick={() => setSelectedResumeId(resume.id)}
                  >
                    <div className="recruit-apply-modal__radio" />
                    <div className="recruit-apply-modal__resume-title">{resume.title || '제목 없음'}</div>
                    <div className="recruit-apply-modal__resume-date">{resume.updatedAt}</div>
                  </div>
                ))}
              </div>
              <ApplyConsent church={church} kind="resume" reserve={reserve} checked={consent} onChange={setConsent} />
            </>
          )}
        </div>

        <div className="recruit-apply-modal__footer">
          <button type="button" className="recruit-apply-modal__btn recruit-apply-modal__btn--secondary" onClick={onClose}>
            취소
          </button>
          {tab === 'file' ? (
            <button type="button" className="recruit-apply-modal__btn recruit-apply-modal__btn--primary" disabled={submitting} onClick={handleFileSubmit}>
              {submitting ? '접수 중...' : reserve ? '관심 표시 남기기' : '지원하기'}
            </button>
          ) : isLogin && resumeList.length > 0 ? (
            <button
              type="button"
              className="recruit-apply-modal__btn recruit-apply-modal__btn--primary"
              disabled={!selectedResumeId || !consent || submitting}
              onClick={handleResumeSubmit}
            >
              {submitting ? '지원 중...' : '지원하기'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
